<script setup lang="ts">
import { reactive, ref } from "vue";
import { api, type AccountPayload, type OnboardingInput } from "../api";
import { authClient, authErrorMessage } from "../auth-client";

const props = defineProps<{ account: AccountPayload }>();
const emit = defineEmits<{ completed: [account: AccountPayload] }>();
const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
const form = reactive<OnboardingInput>({
  display_name: props.account.user.name,
  timezone: detectedTimezone,
  unit_system: props.account.athlete.unit_system,
  training_days_per_week: 4,
  session_minutes: 55,
  experience_level: "intermediate",
  goals: "Build strength and muscle while keeping workouts efficient.",
  available_equipment: "Full commercial gym.",
  movement_constraints: "",
  variety_preference: "balanced"
});
const busy = ref(false);
const error = ref("");

async function submit() {
  busy.value = true;
  error.value = "";
  try {
    const authResult = await authClient.updateUser({ name: form.display_name });
    if (authResult.error) {
      error.value = authErrorMessage(authResult.error, "Could not update your account.");
      return;
    }
    emit("completed", await api.saveOnboarding(form));
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Could not save your training setup.";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <main class="onboarding-shell">
    <header class="onboarding-brand"><div class="brand-mark" aria-hidden="true"><i></i><span></span><i></i></div><b>SUPERSET</b><small>1 minute setup</small></header>
    <form class="onboarding-card" @submit.prevent="submit">
      <div class="onboarding-intro"><p class="eyebrow">Build your planning context</p><h1>Train like you.</h1><p>These defaults shape manual plans and give your AI coach useful context. You can change them anytime.</p></div>

      <section class="onboarding-section">
        <header><span>01</span><div><h2>The basics</h2><p>How your log should look and feel.</p></div></header>
        <div class="form-grid">
          <label>Name<input v-model="form.display_name" autocomplete="name" required maxlength="100"></label>
          <label>Time zone<input v-model="form.timezone" required maxlength="80"></label>
          <label>Weight units<select v-model="form.unit_system"><option value="lb">Pounds (lb)</option><option value="kg">Kilograms (kg)</option></select></label>
          <label>Training experience<select v-model="form.experience_level"><option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option></select></label>
        </div>
      </section>

      <section class="onboarding-section">
        <header><span>02</span><div><h2>Your week</h2><p>Enough structure to make plans realistic.</p></div></header>
        <div class="form-grid compact">
          <label>Days per week<input v-model.number="form.training_days_per_week" type="number" inputmode="numeric" min="1" max="7" required></label>
          <label>Minutes per workout<input v-model.number="form.session_minutes" type="number" inputmode="numeric" min="15" max="180" step="5" required></label>
          <label class="wide">Exercise variety<select v-model="form.variety_preference"><option value="steady">Keep movements steady</option><option value="balanced">Rotate every few weeks</option><option value="frequent">Frequent substitutions</option></select></label>
        </div>
      </section>

      <section class="onboarding-section">
        <header><span>03</span><div><h2>Coach me well</h2><p>The useful details—not a medical intake form.</p></div></header>
        <div class="form-grid single">
          <label>Goals<textarea v-model="form.goals" rows="3" required maxlength="2000" placeholder="Strength, muscle, conditioning, consistency…"></textarea></label>
          <label>Available equipment<textarea v-model="form.available_equipment" rows="2" required maxlength="2000" placeholder="Full gym, home rack, dumbbells…"></textarea></label>
          <label>Movement constraints <span>optional</span><textarea v-model="form.movement_constraints" rows="2" maxlength="2000" placeholder="Old injuries, movements to avoid, substitutions that work…"></textarea></label>
        </div>
      </section>

      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
      <button class="button primary full" :disabled="busy">{{ busy ? "Saving…" : "Finish setup" }}</button>
      <p class="onboarding-footnote">Your answers stay in your Superset account and are exposed only through your own revocable agent token.</p>
    </form>
  </main>
</template>
